import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MeasurementGroupApplyRevisionRequest } from '../types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { applyRevisionDataSchema, MeasurementGroupApplyRevisionFormType } from './mg-apply-revision-zod-schema';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import Spinner from '@/components/spinner/Spinner';
import { useMutation } from '@tanstack/react-query';
import { saveRevisionFile } from '../constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { Input } from '@/components/ui/input';
import { ROUTES } from '@/constants.ts';

type MeasurementGroupApplyRevisionProps = {
	name: string;
	id: number;
	isOpen: boolean;
	handleToggleForm: () => void;
};

const MeasurementGroupApplyRevisionForm = (props: MeasurementGroupApplyRevisionProps) => {
	const { id, isOpen, handleToggleForm } = props;

	const { isDesktop } = useIsDesktopSize();

	const revalidator = useRevalidator();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const navigate = useNavigate();

	const applyRevisionForm = useForm<MeasurementGroupApplyRevisionFormType>({
		resolver: zodResolver(applyRevisionDataSchema),
		defaultValues: {
			revisionDataFile: undefined
		}
	});

	const { isPending: isUpdating, mutate: saveRevision } = useMutation({
		mutationFn: (payload: MeasurementGroupApplyRevisionRequest) => saveRevisionFile(id, payload),
		mutationKey: ['measurement-groups'],
		onSuccess: () => {
			applyRevisionForm.reset();
			handleCloseForm();
			toast('Measurement group revision task created successfully');
			revalidator.revalidate();
			navigate(ROUTES.tasks);
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleApplyRevision = () => {
		const submitForm = applyRevisionForm.handleSubmit(values => {
			const payload: MeasurementGroupApplyRevisionRequest = {
				revisionDataFile: values.revisionDataFile
			};
			saveRevision(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button
					asChild
					type="button"
					size="sm"
					variant="outline"
					onClick={handleToggleForm}
					className="flex justify-start min-w-32 text-xs"
				>
					<span>
						<Settings />
						Apply Revision
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn(' p-5 overflow-auto md:max-w-xl md:w-xl md:h-full w-full h-5/6')}
			>
				<SheetHeader>
					<SheetTitle>Apply measurement group revision</SheetTitle>
					<SheetDescription>Click save when you're done.</SheetDescription>
				</SheetHeader>

				<Form {...applyRevisionForm}>
					<form className="p-4 flex flex-col gap-3">
						<FormField
							name="revisionDataFile"
							control={applyRevisionForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>File</FormLabel>
									<FormControl>
										<Input
											type="file"
											className="cursor-pointer leading-7"
											onChange={e => field.onChange(e.target.files?.[0])}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</form>
				</Form>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Submit
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>This action will schedule the execution of a new revision data process</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isUpdating}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleApplyRevision}
									disabled={isUpdating}
								>
									{error ? 'Try again ' : 'Submit'}
									{isUpdating && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};

export default MeasurementGroupApplyRevisionForm;
